import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Toaster } from 'sonner';
import axios from 'axios';
import './i18n';
import './App.css';

// Pages
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import CategoriesPage from './pages/CategoriesPage';
import CategoryPage from './pages/CategoryPage';
import VideoPage from './pages/VideoPage';
import PricingPage from './pages/PricingPage';
import ProfilePage from './pages/ProfilePage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentCancelPage from './pages/PaymentCancelPage';
import SeminarsPage from './pages/SeminarsPage';
import CalendarPage from './pages/CalendarPage';
import ShopPage from './pages/ShopPage';
import SeminarSuccessPage from './pages/SeminarSuccessPage';
import GamificationPage from './pages/GamificationPage';
import MySeminars from './pages/MySeminars';
import LiveSeminarsPage from './pages/LiveSeminarsPage';
import LiveSeminarPage from './pages/LiveSeminarPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import NotFoundPage from './pages/NotFoundPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import MigrationBanner from './components/MigrationBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const APP_TITLE = 'MISEL GROUP DIGITAL LAB - ONLINE HAIRDRESSING SEMINARS AND EDUCATION';

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const api = axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });

  useEffect(() => {
    if (token) {
      api.defaults.headers.Authorization = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.Authorization;
    }
  }, [token]);

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (e) {
      console.error('Auth check failed', e);
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    localStorage.setItem('token', response.data.access_token);
    setToken(response.data.access_token);
    setUser(response.data.user);
    return response.data;
  };

  const register = async (email, password, name, preferred_language, profileData = {}) => {
    const response = await api.post('/auth/register', { email, password, name, preferred_language, ...profileData });
    localStorage.setItem('token', response.data.access_token);
    setToken(response.data.access_token);
    setUser(response.data.user);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const updateUser = (nextUser) => {
    if (nextUser) setUser(nextUser);
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    updateUser,
    api,
    isAdmin: user?.role === 'admin'
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Protected Route
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-white">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (adminOnly && user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Language Toggle Component
const LanguageToggle = () => {
  const { i18n } = useTranslation();

  const toggleLanguage = () => {
    const newLang = i18n.language === 'el' ? 'en' : 'el';
    i18n.changeLanguage(newLang);
    localStorage.setItem('lang', newLang);
  };

  return (
    <button
      onClick={toggleLanguage}
      data-testid="language-toggle"
      className="flex items-center gap-1 px-3 py-1.5 rounded-sm bg-white/5 hover:bg-white/10 border border-white/10 text-sm font-medium transition-colors"
    >
      <span className={i18n.language === 'el' ? 'text-white' : 'text-white/50'}>EL</span>
      <span className="text-white/30">/</span>
      <span className={i18n.language === 'en' ? 'text-white' : 'text-white/50'}>EN</span>
    </button>
  );
};

export { LanguageToggle, API, BACKEND_URL };

function App() {
  useEffect(() => {
    document.title = APP_TITLE;
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="App min-h-screen bg-background">
          <MigrationBanner />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/categories" element={<CategoriesPage />} />
            <Route path="/category/:slug" element={<CategoryPage />} />
            <Route path="/video/:slug" element={<VideoPage />} />
            <Route path="/seminars" element={<SeminarsPage />} />
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/shop" element={<ShopPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/achievements" element={<GamificationPage />} />
            <Route path="/my-seminars" element={
              <ProtectedRoute>
                <MySeminars />
              </ProtectedRoute>
            } />
            <Route path="/profile" element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            } />
            <Route path="/payment/success" element={<PaymentSuccessPage />} />
            <Route path="/payment/cancel" element={<PaymentCancelPage />} />
            <Route path="/seminars/success" element={<SeminarSuccessPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/live" element={<LiveSeminarsPage />} />
            <Route path="/live/:slug" element={<LiveSeminarPage />} />
            <Route path="/admin/*" element={
              <ProtectedRoute adminOnly>
                <AdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <Toaster 
            position="top-right" 
            theme="dark"
            toastOptions={{
              style: {
                background: '#121212',
                border: '1px solid rgba(255,255,255,0.1)',
                color: '#fff'
              }
            }}
          />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
